import { Button, Stack, Text, tokens } from '@hotjar/hotsauce';
import { useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { SiteId } from '@hotjar/site';
import hateImg from './images/hate.svg';
import neutralImg from './images/neutral.svg';
import loveImg from './images/love.svg';

const SENTIMENT_IMAGES = {
  hate: hateImg,
  dislike: hateImg,
  love: loveImg,
  like: loveImg,
  neutral: neutralImg,
};

export const Wrapper = styled.div<{ score?: string }>`
  width: 288px;
  padding: ${tokens.spacer.x2};
  background-color: ${tokens.color.bg_hover};
  border: 1px solid ${tokens.color.ui_border_light};
  border-radius: ${tokens.radius.l};

  ${({ score }) => {
    if (score === 'hate' || score === 'dislike')
      return `background-color: ${tokens.color.bg_danger};`;
    if (score === 'love' || score === 'like')
      return `background-color: ${tokens.color.bg_success};`;
  }}
`;

const StyledButton = styled(Button)`
  margin-left: -${tokens.spacer.x1};
  padding: 0 ${tokens.spacer.x1};
  align-self: start;
`;

const StyledImg = styled.img`
  height: 30px;
`;

const StyledScore = styled(Text)<{ isPositive?: boolean; isNegative?: boolean; score?: string }>`
  display: inline;
  ${({ score }) => {
    if (score === 'hate' || score === 'dislike') return `color: ${tokens.color.text_danger};`;
    if (score === 'love' || score === 'like') return `color: ${tokens.color.text_success};`;
  }}
`;

const ScoreText = styled.div`
  display: inline;
  h5 {
    display: inline;
  }
`;

const FadeIn = styled(Stack)`
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  animation: fadeIn 0.2s;

  align-items: center;
`;

export const EmptyFeedbackCard = ({
  siteId,
  onClick,
}: {
  siteId: SiteId;
  onClick: () => void;
}): JSX.Element => {
  const { t } = useTranslationWithPrefix('feedback:feedback-card');

  const newFeedbackPath = `/sites/${siteId}/feedback/create/`;

  // wrapping the callback to remove the event params
  const onButtonClicked = useCallback(() => onClick(), [onClick]);

  return (
    <Wrapper>
      <FadeIn direction="row">
        <Stack direction="column" spacing="x1">
          <Text textType="caption" textColor="text_light">
            {t('empty.title')}
          </Text>
          <ScoreText>
            <Text as="h5" textType="header5" textColor="text_default">
              {t('empty.header')}
            </Text>
          </ScoreText>
          <StyledButton
            kind="ghost"
            href={newFeedbackPath}
            onClick={onButtonClicked}
            icon="plus"
            size="s"
            as="a"
          >
            {t('empty.button')}
          </StyledButton>
        </Stack>

        <Stack direction="row" spacing="x0">
          <StyledImg src={hateImg} alt={t('images-alt.hate')} />
          <StyledImg src={neutralImg} alt={t('images-alt.neutral')} />
          <StyledImg src={loveImg} alt={t('images-alt.love')} />
        </Stack>
      </FadeIn>
    </Wrapper>
  );
};

export type Sentiment = 'love' | 'like' | 'neutral' | 'dislike' | 'hate';

export const ScoreFeedbackCard = ({
  sentiment,
  siteId,
  widgetId,
  pageUrl,
  pageOperation,
  onClick,
}: {
  sentiment: Sentiment;
  siteId: SiteId;
  widgetId: number;
  pageUrl: string;
  pageOperation: 'simple_match' | 'exact';
  onClick: (namedScore: Sentiment) => void;
}): JSX.Element => {
  const { t } = useTranslationWithPrefix('feedback:feedback-card');

  const responsesListUrl = `sites/${siteId}/feedback/responses/${widgetId}?date=last-3-months&${
    pageOperation === 'exact' ? 'url' : 'url_contains'
  }=${pageUrl}`;

  const onButtonClicked = useCallback(() => onClick(sentiment), [onClick, sentiment]);

  return (
    <Wrapper score={sentiment}>
      <FadeIn direction="row">
        <Stack direction="column" spacing="x1">
          <Text textType="caption" textColor="text_light">
            {t('scored.time-range-title')}
          </Text>
          <ScoreText>
            <Text as="h5" textType="header5" textColor="text_default">
              {t('scored.users')}
            </Text>
            <StyledScore as="h5" textType="header5" score={sentiment}>
              {t(`scored.sentiment-labels.${sentiment}`)}
            </StyledScore>
            <Text as="h5" textType="header5" textColor="text_default">
              {t('scored.this-page')}
            </Text>
          </ScoreText>

          <StyledButton
            kind="ghost"
            href={responsesListUrl}
            onClick={onButtonClicked}
            icon="arrow_right_alone"
            size="s"
            as="a"
          >
            {t('scored.button')}
          </StyledButton>
        </Stack>

        <Stack direction="row" spacing="x0">
          <img src={SENTIMENT_IMAGES[sentiment]} alt={t(`images-alt.${sentiment}`)} />
        </Stack>
      </FadeIn>
    </Wrapper>
  );
};
